import { useCallback } from 'react';
import type { PlaceholderOptions,
  LinkOptions } from 'remirror/extensions';
import {
  DocExtension, TextExtension,
  ParagraphExtension,
  PlaceholderExtension,
  BoldExtension, ItalicExtension,
  LinkExtension, UnderlineExtension,
  BulletListExtension, OrderedListExtension,
} from 'remirror/extensions';
import type { InvalidContentHandler } from '@remirror/core';
import { useRemirror, ReactComponentExtension } from '@remirror/react';
import type { RootNode } from '@trinity/chat/src/types/rich-text';
import type { ImageOptions} from '../../Remirror';
import { ImageExtension, TextColorExtension } from '../../Remirror';

type Props = {
  initialState?: RootNode;
  placeholder?: string;
};

export const useBasicRichTextEditor = ({ initialState, placeholder }: Props) => {

  const onError: InvalidContentHandler = useCallback(({ json, invalidContent, transformers }) => {
    console.error('BasicRichText: invalid content detected, attempting recovery', invalidContent);
    const fixed = transformers.remove(json, invalidContent);
    // After removing invalid nodes, the doc may be empty which violates the
    // `block+` schema constraint and would trigger another error → infinite
    // recursion until Remirror hits MAX_ATTEMPTS. Return a valid empty doc
    // instead.
    if (!fixed.content || fixed.content.length === 0) {
      return { type: 'doc', content: [{ type: 'paragraph' }] };
    }
    return fixed;
  }, []);

  const imageExtensionParams: ImageOptions = {
    enableResizing: true,
  };

  const linkExtensionParams: LinkOptions = {
    autoLink: true,
    defaultTarget: '_blank',
  };

  const placeholderExtensionParams: PlaceholderOptions = {
    placeholder,
  };

  return useRemirror({
    content: initialState,
    extensions: () => [
      new DocExtension({}),
      new PlaceholderExtension(placeholderExtensionParams),
      new ParagraphExtension({}),
      new TextExtension(),
      new ReactComponentExtension({}),
      new BoldExtension({}),
      new ItalicExtension(),
      new UnderlineExtension(),
      new TextColorExtension({}),
      new LinkExtension(linkExtensionParams),
      new ImageExtension(imageExtensionParams),
      new BulletListExtension({}),
      new OrderedListExtension({}),
    ],
    onError,
  });

};
